import { ajax } from "lib/observable";
import { Observable } from "rxjs";
import "rxjs/add/operator/map";
import { APIError } from "lib/errors";
import {
    WHITEBOARD_PAGE_FETCH,
    WHITEBOARD_PAGE_FETCH_COMPLETED,
    WHITEBOARD_PAGE_MOVE,
    WHITEBOARD_PAGE_REMOVE,
    WHITEBOARD_PAGES_CREATE,
} from "constants/action-types";
import siteConfig from "site-config";
import { listInsertOperation, listMoveOperation, listRemoveOperation, mapCreate } from "actions/mutate";

import { pages } from "actions/whiteboard";

import { getComponentType } from "constants/whiteboard";

const { pageHistoryFetchComplete } = pages;
const parseResponse = ({ response }: any) => {
    const page = response.page;
    let components = {};
    if (page.snapshot) {
        components = page.snapshot.components || {};
    }
    Object.keys(components).forEach((key) => {
        page.snapshot.components[key].component = getComponentType(page.snapshot.components[key] as any);
    });
    return page;
};

export const createEpic = (action$, store) =>
    action$
        .ofType(WHITEBOARD_PAGES_CREATE)
        .map(() => {
            const { whiteboard, mutate } = store.getState();
            const pages = mutate.snapshots.get(whiteboard.pagesId);
            return { boardId: whiteboard.boardId, pagesId: whiteboard.pagesId, index: pages.size };
        })
        .concatMap(({ boardId, pagesId, index }) =>
            ajax({ url: `${siteConfig.goAPI}/v1/boards/${boardId}/pages`, method: "POST", body: {} })
                .map(parseResponse)
                .flatMap((page) => {
                    let components = {};
                    if (page.snapshot) {
                        components = page.snapshot.components || {};
                    }
                    return [
                        {
                            payload: page,
                            type: WHITEBOARD_PAGE_FETCH_COMPLETED,
                        },
                        mapCreate("nuorder.service.board.page", page.ref.object_id, components, page.ref),
                        listInsertOperation(pagesId, page, index, { buffer: false }),
                    ];
                })
                .catch((error) => {
                    return Observable.of({
                        error: true,
                        payload: error,
                        type: WHITEBOARD_PAGE_FETCH_COMPLETED,
                    });
                })
        );

export const fetchEpic = (action$, store) =>
    action$
        .ofType(WHITEBOARD_PAGE_FETCH)
        .map(({ payload, meta }) => {
            const { mutate, whiteboard } = store.getState();
            return { boardId: whiteboard.boardId, pageId: payload, mutate, meta };
        })
        .switchMap(({ pageId, boardId, mutate, meta }) => {
            if (mutate.HEADs.has(pageId) && meta.force !== true) {
                return Observable.of(
                    pageHistoryFetchComplete({
                        meta,
                        payload: {
                            ref: mutate.HEADs.get(pageId),
                            snapshot: mutate.snapshots.get(pageId),
                        },
                    })
                );
            } else {
                return ajax(`${siteConfig.goAPI}/v1/boards/${boardId}/pages/${pageId}?with_snapshot=true`)
                    .map(parseResponse)
                    .flatMap((page) => {
                        let components = {};
                        if (page.snapshot) {
                            components = page.snapshot.components || {};
                        }
                        return [
                            {
                                meta,
                                payload: page,
                                type: WHITEBOARD_PAGE_FETCH_COMPLETED,
                            },
                            mapCreate("nuorder.service.board.page", page.ref.object_id, components, page.ref),
                        ];
                    })
                    .catch((error) => {
                        return Observable.of({
                            error: true,
                            payload: new APIError(error.xhr.response.error, error.xhr.status, error.xhr.statusText),
                            type: WHITEBOARD_PAGE_FETCH_COMPLETED,
                        });
                    });
            }
        });

export const removeEpic = (action$, store) =>
    action$
        .ofType(WHITEBOARD_PAGE_REMOVE)
        .map(({ payload }) => {
            const { index, id } = payload;
            const { whiteboard, mutate } = store.getState();
            const { pagesId, currentPageId } = whiteboard;
            const snapshot = mutate.snapshots.get(pagesId);
            return { pagesId, index, id, currentPageId, snapshot };
        })
        .flatMap(({ pagesId, index, id, currentPageId, snapshot }) => {
            const actions = [listRemoveOperation(pagesId, index, { buffer: false })];

            // Change page if equal to current page
            if (snapshot && id === currentPageId) {
                actions.push(
                    pages.current.set(
                        snapshot
                            .get(
                                snapshot.size === index + 1 // if last item
                                    ? index - 1
                                    : index + 1
                            )
                            .getIn(["ref", "object_id"])
                    ) as any
                );
            }

            return actions;
        });

// We translate this to a generic action
export const moveEpic = (action$, store) =>
    action$
        .ofType(WHITEBOARD_PAGE_MOVE)
        .map(({ payload }) => {
            const { destination_index, index } = payload;
            const { whiteboard } = store.getState();
            const { pagesId } = whiteboard;
            return { destination_index, index, pagesId };
        })
        .map(({ destination_index, index, pagesId }) =>
            listMoveOperation(pagesId, index, destination_index, { buffer: false })
        );
